import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ConfigConstants, isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCalendar, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import moment, { HTML5_FMT } from 'moment';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    pairwise,
    startWith,
    switchMap,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import {
    updateReplacementOrLastProcedureDateSelection,
    updateReplacementOrLastProcedureDateValue,
} from '../../store/actions/functional-elements.actions';
import {
    getFormCheckpointByXmlIdFactory,
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormRadioOptionsByXmlIdFactory,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Checkpoint } from '../../types/checkpoint';
import { ContentId } from '../../types/content-block';
import { FormFilters } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { ParticipantCopyId } from '../../types/participant-copy';
import { RADIO_OPTION_TYPE, RadioOption } from '../../types/radio-option';
import {
    REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION,
    ReplacementOrLastProcedureDate,
} from '../../types/replacement-or-last-procedure-date';
import { isAstoriaFunctionalElementVisible } from '../../utilities/answer-utilities';

@Component({
    selector: 'pace-forms-replacement-or-last-procedure-date',
    templateUrl: './replacement-or-last-procedure-date.component.html',
    styleUrls: ['./replacement-or-last-procedure-date.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReplacementOrLastProcedureDateComponent implements OnInit, OnDestroy {
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    public readOnly$: Observable<boolean>;
    public replacementDate$: Observable<ReplacementOrLastProcedureDate>;
    public controllingCheckpoint$: Observable<Checkpoint | undefined>;
    public options$: Observable<RadioOption[] | undefined>;
    public selectedParticipant$: Observable<ParticipantCopyId>;

    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public visible$: Observable<boolean>;

    private destroyTrigger$ = new Subject<void>();

    public replacementDateGroup: FormGroup;
    public dateInputControl: FormControl;
    public radioInputControl: FormControl;
    public checkboxControl: FormControl;

    public faCheck = faCheck;
    public faCalendar = faCalendar;

    public readonly startupDateOption = 'startupDate';
    public readonly replacementDateOption = 'replacementDate';

    // make enum accessible in HTML
    public REPLACEMENT_DATE_SELECTION = REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
    }

    public ngOnInit(): void {
        this.replacementDateGroup = this.formBuilder.group({});
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.replacementDateGroup);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'ReplacementDateComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }

        this.readOnly$.pipe(take(1)).subscribe((readOnly) => {
            this.dateInputControl = this.formBuilder.control({ value: '', disabled: readOnly });
            this.radioInputControl = this.formBuilder.control({ value: '', disabled: readOnly });
            this.checkboxControl = this.formBuilder.control({ value: false, disabled: readOnly });
            this.replacementDateGroup.addControl('date', this.dateInputControl);
            this.replacementDateGroup.addControl('radio', this.radioInputControl);
            this.replacementDateGroup.addControl('checkbox', this.checkboxControl);
        });

        this.replacementDate$ = this.store
            .select(getFormFunctionalElementByIdFactory<ReplacementOrLastProcedureDate>({ id: this.id }))
            .pipe(filter(isDefined));

        this.controllingCheckpoint$ = this.replacementDate$.pipe(
            mergeMap((replacementDate) =>
                replacementDate.controllingCheckpoint.length > 0
                    ? this.store
                          .select(getFormCheckpointByXmlIdFactory({ xmlId: replacementDate.controllingCheckpoint }))
                          .pipe(take(1))
                    : of(undefined),
            ),
        );

        this.options$ = this.controllingCheckpoint$.pipe(
            switchMap((checkpoint) =>
                typeof checkpoint !== 'undefined'
                    ? this.store.select(getFormRadioOptionsByXmlIdFactory({ xmlId: checkpoint.xmlId }))
                    : of(undefined),
            ),
        );

        this.visible$ = combineLatest([
            this.replacementDate$,
            this.controllingCheckpoint$,
            this.options$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(([replacementDate, checkpoint, options, typeFilters, formFilters, selectedParticipant]) => {
                // The  master displays everything. In a participant copy, we display only content that was filled out in
                // the  current session and not loaded from XML.
                let visible =
                    selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID ||
                    !replacementDate.contentLoadedFromXml;

                // In a ddition to the rule above, a controlling checkpoint can control the visibility by either the type
                // of t he checkpoint and a selected matching filter, or by the chosen answer: In a participant copy, we
                // disp lay only values answered with "yes".
                const visibleByFilter = isAstoriaFunctionalElementVisible(
                    replacementDate,
                    checkpoint?.elementType,
                    typeFilters,
                    formFilters,
                    checkpoint?.xmlId ? { [checkpoint.xmlId]: options } : {},
                );
                visible = visibleByFilter && visible;
                if (typeof checkpoint !== 'undefined' && typeof options !== 'undefined') {
                    const selectedInMaster = options.find((o) => o.selectedInMaster);
                    const yesInMaster =
                        typeof selectedInMaster !== 'undefined' &&
                        selectedInMaster.optionType === RADIO_OPTION_TYPE.YES &&
                        !selectedInMaster.contentLoadedFromXml;
                    visible =
                        visible &&
                        // The  master displays everything. Participant copies only show content that was not loaded from
                        // an i nterim XML and that was answered with YES.
                        (selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID || yesInMaster);
                }

                return visible;
            }),
        );

        combineLatest([this.replacementDate$, this.readOnly$])
            .pipe(takeUntil(this.destroyTrigger$))
            .subscribe(([replacementDate, readOnly]) => {
                const date =
                    replacementDate.date.length > 0 && replacementDate.date !== FormsConstants.NA
                        ? moment(replacementDate.date)
                        : moment.invalid();
                this.dateInputControl.patchValue(date.isValid() ? date.format(HTML5_FMT.DATE) : '');
                this.dateInputControl[replacementDate.disabled || readOnly ? 'disable' : 'enable']({
                    emitEvent: false,
                });
                this.radioInputControl.patchValue(replacementDate.selected);
                this.radioInputControl[replacementDate.disabled || readOnly ? 'disable' : 'enable']({
                    emitEvent: false,
                });

                this.cdr.detectChanges();
            });

        this.dateInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we u se take(1) because we are only interested in static properties of replacementDate$
                withLatestFrom(this.replacementDate$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newVal, replacementDate]: [string, ReplacementOrLastProcedureDate]) => {
                const newDate = moment(newVal);
                this.store.dispatch(
                    updateReplacementOrLastProcedureDateValue({
                        id: replacementDate.id,
                        dayXmlId: replacementDate.dayXmlId,
                        monthXmlId: replacementDate.monthXmlId,
                        yearXmlId: replacementDate.yearXmlId,
                        newValue: newDate.isValid() ? newDate.toISOString(true) : '',
                    }),
                );
            });

        this.radioInputControl.valueChanges
            .pipe(distinctUntilChanged(), withLatestFrom(this.replacementDate$), takeUntil(this.destroyTrigger$))
            .subscribe(
                ([value, replacementDate]: [
                    REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION | null,
                    ReplacementOrLastProcedureDate,
                ]) => {
                    const selected = value === null ? REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE : value;

                    // this needs to be taken out of the state because we might have switched participant copies and then
                    // the  previousSelection store in the component might not correctly reflect the actual previous
                    // sele ction for that participant copy
                    if (selected === replacementDate.selected) {
                        return;
                    }

                    this.store.dispatch(
                        updateReplacementOrLastProcedureDateSelection({
                            xmlId: replacementDate.radioButtonXmlId,
                            id: replacementDate.id,
                            selected: selected,
                        }),
                    );
                },
            );

        this.checkboxControl.valueChanges
            .pipe(
                // the  combination of startWith and pairWise enables us to access the previous and the current value in
                // the  observable
                startWith(false),
                pairwise(),
                // we u se take(1) because we are only interested in static properties of materialSerialNumber$
                withLatestFrom(this.replacementDate$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([[previousChecked, currentChecked], replacementDate]) => {
                if (previousChecked !== currentChecked) {
                    this.store.dispatch(
                        updateReplacementOrLastProcedureDateValue({
                            id: this.id,
                            newValue: '',
                            dayXmlId: replacementDate.dayXmlId,
                            monthXmlId: replacementDate.monthXmlId,
                            yearXmlId: replacementDate.yearXmlId,
                        }),
                    );
                    this.store.dispatch(
                        updateReplacementOrLastProcedureDateSelection({
                            id: this.id,
                            selected: REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE,
                            xmlId: replacementDate.radioButtonXmlId,
                        }),
                    );
                }
                this.radioInputControl[currentChecked ? 'disable' : 'enable']({ emitEvent: false });
                this.dateInputControl[currentChecked ? 'disable' : 'enable']({ emitEvent: false });
            });
    }

    public ngOnDestroy(): void {
        // remo ve form controls from parent
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.replacementDateGroup);
            this.parentForm.removeAt(index);
        }
        this.destroyTrigger$.next();
    }

    public setToday(): void {
        this.dateInputControl.setValue(moment().format(HTML5_FMT.DATE));
    }

    public reset(): void {
        this.radioInputControl.reset();
    }
}
